import React, { useEffect, useRef, useState } from 'react';

// Define interfaces for the slide data structure
interface DescriptionType {
    heading: string;
    content: string;
    highlight: string;
    details: string;
}

interface SlideType {
    id: string;
    keyInfoId: string;
    contentWrapperId: string;
    title: string;
    fact: string; // Fact includes HTML
    footer: string; // Footer includes HTML
    description: DescriptionType;
}

interface KeyOutcomesProps {
    slides: SlideType[];
}

const KeyOutcomesSlider: React.FC<KeyOutcomesProps> = ({ slides }) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [offsetX, setOffsetX] = useState(0);
    const totalSlides = slides.length;
    const [slideWidth, setSlideWidth] = useState(0);
    const slideRef = useRef<HTMLDivElement | null>(null);
    const touchStartX = useRef(0);
    const isDragging = useRef(false);

    const handleNextSlide = () => {
        if (currentSlide < totalSlides - 1) {
            setCurrentSlide((prevSlide) => prevSlide + 1);
            setOffsetX(0);
        }
    };

    const handlePrevSlide = () => {
        if (currentSlide > 0) {
            setCurrentSlide((prevSlide) => prevSlide - 1);
            setOffsetX(0);
        }
    };

    const updateSlideWidth = () => {
        if (slideRef.current) {
            const width = slideRef.current.clientWidth;
            setSlideWidth(width);
        }
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        const touch = e.touches[0];
        if (touch) {
            touchStartX.current = touch.clientX;
            isDragging.current = true;
        }
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!isDragging.current) return;
        const touch = e.touches[0];
        if (touch) {
            const touchMoveX = touch.clientX;
            const deltaX = touchMoveX - touchStartX.current;

            // İlk slayttayken sola, son slayttayken sağa fazla kaymayı sınırlıyoruz
            if ((currentSlide === 0 && deltaX > 0) || (currentSlide === totalSlides - 1 && deltaX < 0)) {
                setOffsetX(deltaX / 4); // Sınırlı kaydırma efekti için direnç ekliyoruz
            } else {
                setOffsetX(deltaX);
            }
        }
    };

    const handleTouchEnd = () => {
        isDragging.current = false;

        if (offsetX < -slideWidth / 4 && currentSlide < totalSlides - 1) {
            handleNextSlide();
        } else if (offsetX > slideWidth / 4 && currentSlide > 0) {
            handlePrevSlide();
        } else {
            setOffsetX(0); // Yetersiz kaydırma yapıldığında geri ortalar
        }
    };

    useEffect(() => {
        updateSlideWidth();
        window.addEventListener('resize', updateSlideWidth);

        return () => {
            window.removeEventListener('resize', updateSlideWidth);
        };
    }, []);

    return (
        <section className="section-key-outcomes">
            <div
                className="key-outcome-slider w-slider"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                <div className="testimonial-mask w-slider-mask">
                    {slides.map((slide, index) => {
                        const isActive = index === currentSlide;

                        return (
                            <div key={slide.id} id={slide.id}
                                className={`infographics-slide w-slide ${isActive ? 'active' : ''}`}
                                style={{
                                    // transform: `translateX(${-(currentSlide * slideWidth)}px)`,
                                    transform: `translateX(${-currentSlide * slideWidth + offsetX}px)`,
                                    transition: isDragging.current ? 'none' : 'transform 500ms ease',
                                    opacity: isActive ? 1 : 0.5,
                                }}
                                ref={index === 0 ? slideRef : null}
                            >
                                <div className="infographic-card">
                                    <div className="w-layout-grid testimonial-grid">
                                        <div id={slide.keyInfoId} className="key-info-div">
                                            <p className="all-caps-info-text">{slide.title}</p>
                                            <p
                                                className="key-fact"
                                                dangerouslySetInnerHTML={{ __html: slide.fact }} // Render HTML for number fact
                                            />
                                            <div className="content-footer">
                                                <div className="icon-regular w-embed">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="16"
                                                        height="16"
                                                        viewBox="0 0 16 16"
                                                        fill="none"
                                                    >
                                                        <path
                                                            d="M7.99967 14.6666C4.31777 14.6666 1.33301 11.6818 1.33301 7.99998C1.33301 4.31808 4.31777 1.33331 7.99967 1.33331C11.6815 1.33331 14.6663 4.31808 14.6663 7.99998C14.6663 11.6818 11.6815 14.6666 7.99967 14.6666ZM7.99967 13.3333C10.9452 13.3333 13.333 10.9455 13.333 7.99998C13.333 5.05446 10.9452 2.66665 7.99967 2.66665C5.05415 2.66665 2.66634 5.05446 2.66634 7.99998C2.66634 10.9455 5.05415 13.3333 7.99967 13.3333ZM7.33301 4.66665H8.66634V5.99998H7.33301V4.66665ZM7.33301 7.33331H8.66634V11.3333H7.33301V7.33331Z"
                                                            fill="#ffffff"
                                                        />
                                                    </svg>
                                                </div>
                                                <div
                                                    className="paragraph-x-small full-white"
                                                    dangerouslySetInnerHTML={{ __html: slide.footer }} // Render HTML for footer
                                                />
                                            </div>
                                        </div>
                                        <div id={slide.contentWrapperId} className="testimonial-content-wrapper">
                                            <div className="wrap-v-large">
                                                <p className="tagline-heading">
                                                    <strong>{slide.description.heading}</strong>
                                                </p>
                                                <p className="description white">{slide.description.content}</p>
                                                <div className="h6-heading turqoise">
                                                    {slide.description.highlight}
                                                </div>
                                            </div>
                                            <div className="paragraph-regular light">{slide.description.details}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
                <div
                    className="generic-slider-trigger left dark-blue w-slider-arrow-left"
                    onClick={handlePrevSlide}
                    aria-hidden={currentSlide === 0 ? "true" : "false"}
                >
                    <div className="slider-chevron"></div>
                </div>
                <div
                    className="generic-slider-trigger right dark-blue w-slider-arrow-right"
                    onClick={handleNextSlide}
                    aria-hidden={currentSlide === totalSlides - 1 ? "true" : "false"}
                >
                     <div className="slider-chevron"></div>
                </div>
                <div className="slide-nav---hidden w-slider-nav w-round"></div>
            </div>
        </section>
    );
};

export default KeyOutcomesSlider;
